/**
 * UserInfo - A molecule component for displaying user information.
 *
 * Displays user name and email with proper truncation.
 * Used in user menus and profile displays.
 */

import { cn } from '@lean-agility/design-system/react';
import type { AuthUser } from '../../types';
import { Avatar } from '@/components/atoms/Avatar';

export interface UserInfoProps {
  /**
   * The user to display information for.
   */
  user: AuthUser;
  /**
   * Whether to show a border separator below.
   */
  showBorder?: boolean;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

export function UserInfo({ user, showBorder = true, className }: UserInfoProps) {
  // Use name if available, otherwise derive from email (part before @)
  const displayName = user.name || user.email.split('@')[0] || user.email;

  return (
    <div
      className={cn(
        'px-4 py-3',
        showBorder &&
          `
            border-b border-border-default
            dark:border-border-default
          `,
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <Avatar alt="User avatar" />
        <div className="min-w-0 flex-1">
          <p
            className="
              truncate text-sm font-medium text-text-primary
              dark:text-text-primary
            "
          >
            {displayName}
          </p>
          <p
            className="
              truncate text-xs text-text-muted
              dark:text-text-muted
            "
          >
            {user.email}
          </p>
        </div>
      </div>
    </div>
  );
}
